import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Seo from '../components/Seo';
import { cvSchema } from '../components/structuredData';
import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  Chip,
  Avatar,
  Button,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  Slide,
  IconButton,
  Drawer,
  ListItemButton,
  Fade,
} from '@mui/material';
import {
  LocationOn,
  GitHub,
  LinkedIn,
  Work,
  School,
  Code,
  Language,
  EmojiObjects,
  ArrowBack,
  Person,
  Menu as MenuIcon,
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';

// Import CV data and types
import cvDataJson from '../data/cvData.json';
import { CVData } from '../types/cvTypes';

// Type assertion for imported JSON data
const cvData: CVData = cvDataJson as CVData;

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Styled Components
const CVContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: '#1a1a1a',
  color: '#f0f0f0',
}));

const HeroSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 50%, #2d2d2d 100%)',
  padding: theme.spacing(8, 0),
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.1)',
  }
}));

const SectionCard = styled(Paper)(({ theme }) => ({
  backgroundColor: '#2a2a2a',
  borderRadius: '16px',
  padding: theme.spacing(4),
  marginBottom: theme.spacing(4),
  border: '1px solid #404040',
  transition: 'all 0.3s ease',
  animation: `${fadeInUp} 0.6s ease-out`,
  '&:hover': {
    borderColor: '#606060',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#f0f0f0',
  fontWeight: '700',
  marginBottom: theme.spacing(3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  '&::after': {
    content: '""',
    flex: 1,
    height: '2px',
    background: 'linear-gradient(90deg, #666666 0%, transparent 100%)',
    marginLeft: theme.spacing(2),
  }
}));

const NavigationBar = styled(AppBar)(({ theme }) => ({
  background: 'rgba(26, 26, 26, 0.95)',
  backdropFilter: 'blur(20px)',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: '#e0e0e0',
  textTransform: 'none',
  fontWeight: '500',
  padding: '8px 16px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    color: 'white',
    transform: 'translateY(-1px)',
  }
}));

const SkillChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  borderRadius: '20px',
  backgroundColor: '#404040',
  color: '#e0e0e0',
  fontWeight: '500',
  border: '1px solid #505050',
  '&:hover': {
    backgroundColor: '#505050',
    color: '#f0f0f0',
    transform: 'scale(1.05)',
    transition: 'all 0.2s ease',
  }
}));

const CVPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showNavbar, setShowNavbar] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowNavbar(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <CVContainer>
      <Seo
        title={`CV & Skills — ${cvData.personalInfo.name}`}
        description={cvData.personalInfo.summary}
        path="/cv"
        structuredData={cvSchema}
      />
      {/* Navigation Bar */}
      <Slide direction="down" in={showNavbar} mountOnEnter unmountOnExit>
        <NavigationBar position="fixed" elevation={0}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: '600' }}>
              {cvData.personalInfo.name}
            </Typography>
            {isMobile ? (
              <>
                <IconButton
                  edge="end"
                  color="inherit"
                  aria-label="menu"
                  onClick={() => setDrawerOpen(true)}
                  sx={{ color: '#e0e0e0' }}
                >
                  <MenuIcon />
                </IconButton>
                <Drawer
                  anchor="right"
                  open={drawerOpen}
                  onClose={() => setDrawerOpen(false)}
                  PaperProps={{ sx: { background: 'linear-gradient(135deg, #232526 0%, #1a1a1a 100%)', minWidth: 270, boxShadow: '0 0 40px 10px #000a' } }}
                >
                  <Box sx={{ width: 270, pt: 0, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      px: 2,
                      py: 3,
                      background: 'rgba(34, 40, 49, 0.95)',
                      borderBottom: '1px solid rgba(255,255,255,0.07)',
                      position: 'relative',
                      zIndex: 2
                    }}>
                      <Avatar src={cvData.personalInfo.avatar} sx={{ width: 48, height: 48, border: '2px solid #66bb6a', boxShadow: '0 2px 12px #66bb6a44' }} />
                      <Box>
                        <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700, letterSpacing: 0.5 }}>{cvData.personalInfo.name}</Typography>
                        <Typography variant="caption" sx={{ color: '#66bb6a', fontWeight: 500 }}>{cvData.personalInfo.title}</Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1 }} />
                      <IconButton onClick={() => setDrawerOpen(false)} sx={{ color: '#e0e0e0' }}>
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <List sx={{ mt: 2, zIndex: 2, position: 'relative' }}>
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '100ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton component={RouterLink} to="/" onClick={() => setDrawerOpen(false)} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(102,187,106,0.08)' } }}>
                            <ListItemIcon><Work sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>Portfolio</span>} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '200ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => { setDrawerOpen(false); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 200); }} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(255,255,255,0.06)' } }}>
                            <ListItemIcon><Person sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>CV & Skills</span>} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>
                      <Box sx={{ px: 2, py: 1, mt: 1, mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: '#b0b0b0', fontWeight: 500, letterSpacing: 1, textTransform: 'uppercase' }}>
                          External Links
                        </Typography>
                      </Box>
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '300ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => { setDrawerOpen(false); setTimeout(() => window.open('https://github.com/VemundFredriksen', '_blank'), 200); }} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(33,150,243,0.08)' } }}>
                            <ListItemIcon><GitHub sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>GitHub</span>} />
                            <OpenInNewIcon fontSize="small" sx={{ color: '#b0b0b0', ml: 1 }} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>
                      <Fade in={drawerOpen} style={{ transitionDelay: drawerOpen ? '400ms' : '0ms' }}>
                        <ListItem disablePadding>
                          <ListItemButton onClick={() => { setDrawerOpen(false); setTimeout(() => window.open('https://www.linkedin.com/in/vemundfredriksen/', '_blank'), 200); }} sx={{ borderRadius: 2, mx: 1, mb: 1, transition: 'all 0.2s', '&:hover': { background: 'rgba(33,150,243,0.08)' } }}>
                            <ListItemIcon><LinkedIn sx={{ color: '#e0e0e0' }} /></ListItemIcon>
                            <ListItemText primary={<span style={{ color: '#e0e0e0', fontWeight: 500 }}>LinkedIn</span>} />
                            <OpenInNewIcon fontSize="small" sx={{ color: '#b0b0b0', ml: 1 }} />
                          </ListItemButton>
                        </ListItem>
                      </Fade>
                    </List>
                  </Box>
                </Drawer>
              </>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <NavButton
                  startIcon={<Work />}
                  component={RouterLink}
                  to="/"
                >
                  Portfolio
                </NavButton>
                <NavButton 
                  startIcon={<Person />}
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  CV & Skills
                </NavButton>
                <IconButton 
                  onClick={() => window.open('https://github.com/VemundFredriksen', '_blank')}
                  sx={{ 
                    color: '#e0e0e0',
                    '&:hover': { color: 'white' }
                  }}
                >
                  <GitHub />
                </IconButton>
                <IconButton 
                  onClick={() => window.open('https://www.linkedin.com/in/vemundfredriksen/', '_blank')}
                  sx={{ 
                    color: '#e0e0e0',
                    '&:hover': { color: 'white' }
                  }}
                >
                  <LinkedIn />
                </IconButton>
              </Box>
            )}
          </Toolbar>
        </NavigationBar>
      </Slide>
      {/* Hero Section */}
      <HeroSection>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
                <Avatar
                  src={cvData.personalInfo.avatar}
                  sx={{
                    width: { xs: 120, md: 160 },
                    height: { xs: 120, md: 160 },
                    border: '4px solid rgba(255, 255, 255, 0.2)',
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Typography variant={isMobile ? "h3" : "h2"} sx={{ fontWeight: 'bold', mb: 1, color: '#f0f0f0', textAlign: 'left' }}>
                {cvData.personalInfo.name}
              </Typography>
              <Typography variant={isMobile ? "h5" : "h4"} sx={{ color: '#b0b0b0', mb: 3, textAlign: 'left' }}>
                {cvData.personalInfo.title}
              </Typography>
              <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7, maxWidth: '600px', color: '#e0e0e0', textAlign: 'left' }}>
                {cvData.personalInfo.summary}
              </Typography> 
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3, justifyContent: 'flex-start' }}>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  component={RouterLink}
                  to="/"
                  sx={{
                    borderColor: '#666666',
                    color: '#e0e0e0',
                    '&:hover': { borderColor: '#777777' }
                  }}
                >
                  Back to Portfolio
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </HeroSection>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Grid container spacing={4}>
          {/* Left Column */}
          <Grid item xs={12} md={8}>
            {/* Professional Experience */}
            <SectionCard>
              <SectionTitle variant="h4">
                <Work />
                Professional Experience
              </SectionTitle>
              <Box sx={{ position: 'relative' }}>
                {/* Timeline Line */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '16px',
                    top: '20px',
                    bottom: '20px',
                    width: '2px',
                    backgroundColor: '#555555',
                    zIndex: 1
                  }}
                />
                {cvData.experience.map((job, index) => (
                  <Box key={index} sx={{ position: 'relative', mb: index < cvData.experience.length - 1 ? 4 : 0 }}>
                    {/* Timeline Dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '8px',
                        top: '12px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#888888',
                        border: '3px solid #2a2a2a',
                        zIndex: 2
                      }}
                    />
                    <Box sx={{ ml: 5, textAlign: 'left' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f0f0f0', textAlign: 'left' }}>
                        {job.position}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ color: '#b0b0b0', mb: 1, textAlign: 'left' }}>
                        {job.company} • {job.location}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#888888', mb: 2, textAlign: 'left' }}>
                        {job.duration}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6, color: '#e0e0e0', textAlign: 'left' }}>
                        {job.description}
                      </Typography>
                      {index < cvData.experience.length - 1 && <Divider sx={{ mt: 3, borderColor: '#404040' }} />}
                    </Box>
                  </Box>
                ))}
              </Box>
            </SectionCard>

            {/* Education */}
            <SectionCard>
              <SectionTitle variant="h4">
                <School />
                Education
              </SectionTitle>
              <Box sx={{ position: 'relative' }}>
                {/* Timeline Line */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: '16px',
                    top: '20px',
                    bottom: '20px',
                    width: '2px',
                    backgroundColor: '#555555',
                    zIndex: 1
                  }}
                />
                {cvData.education.map((edu, index) => (
                  <Box key={index} sx={{ position: 'relative', mb: index < cvData.education.length - 1 ? 4 : 0 }}>
                    {/* Timeline Dot */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: '8px',
                        top: '12px',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        backgroundColor: '#777777',
                        border: '3px solid #2a2a2a',
                        zIndex: 2
                      }}
                    />
                    <Box sx={{ ml: 5, textAlign: 'left' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#f0f0f0', textAlign: 'left' }}>
                    {edu.degree}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: '#b0b0b0', mb: 1, textAlign: 'left' }}>
                    {edu.institution} • {edu.location}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#888888', mb: 2, textAlign: 'left' }}>
                    {edu.duration}
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#e0e0e0', textAlign: 'left' }}>
                    {edu.description}
                  </Typography>
                  {index < cvData.education.length - 1 && <Divider sx={{ mt: 3, borderColor: '#404040' }} />}
                    </Box>
                  </Box>
                ))}
              </Box>
            </SectionCard>
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={4}>
            {/* Contact Information */}
            <SectionCard>
              <SectionTitle variant="h5">
                Contact
              </SectionTitle>
              <List>
                <ListItem sx={{ py: 1 }}>
                  <ListItemIcon>
                    <LocationOn sx={{ color: '#888888' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={cvData.personalInfo.location}
                    primaryTypographyProps={{ variant: 'body2', color: '#e0e0e0' }}
                  />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <ListItemIcon>
                    <GitHub sx={{ color: '#888888' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="GitHub Profile"
                    primaryTypographyProps={{ 
                      variant: 'body2',
                      component: 'a',
                      href: cvData.personalInfo.github,
                      target: '_blank',
                      sx: { 
                        textDecoration: 'none',
                        color: '#e0e0e0',
                        '&:hover': { color: '#f0f0f0' }
                      }
                    }}
                  />
                </ListItem>
                <ListItem sx={{ py: 1 }}>
                  <ListItemIcon>
                    <LinkedIn sx={{ color: '#888888' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="LinkedIn Profile"
                    primaryTypographyProps={{
                      variant: 'body2',
                      component: 'a',
                      href: cvData.personalInfo.linkedin,
                      target: '_blank',
                      sx: {
                        textDecoration: 'none',
                        color: '#e0e0e0',
                        '&:hover': { color: '#f0f0f0' }
                      }
                    }}
                  />
                </ListItem>
              </List>
            </SectionCard>

            {/* Technical Skills */}
            <SectionCard>
              <SectionTitle variant="h5">
                <Code />
                Technical Skills
              </SectionTitle>
              {Object.entries(cvData.technicalSkills).map(([category, skills]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', color: '#f0f0f0', textAlign: 'left' }}>
                    {category}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                    {skills.map((skill) => (
                      <SkillChip key={skill} label={skill} size="small" />
                    ))}
                  </Box>
                </Box>
              ))}
            </SectionCard>

            {/* Certifications */}
            <SectionCard>
              <SectionTitle variant="h5">
                <EmojiObjects />
                Certifications
              </SectionTitle>
              <Box sx={{ textAlign: 'left' }}>
                {cvData.certifications.map((cert, index) => (
                  <Box key={index} sx={{ mb: 2 }}>
                    <Typography variant="body2" sx={{ color: '#f0f0f0', fontWeight: 'bold', mb: 0.5 }}>
                      {cert.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                        {cert.issuer}
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#888888' }}>•</Typography>
                      <Typography variant="caption" sx={{ color: '#b0b0b0' }}>
                        {cert.year}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </SectionCard>

            {/* Languages */}
            <SectionCard>
              <SectionTitle variant="h5">
                <Language />
                Languages
              </SectionTitle>
              {cvData.languages.map((lang) => (
                <Box key={lang.name} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: '#e0e0e0' }}>{lang.name}</Typography>
                  <Typography variant="body2" sx={{ color: '#b0b0b0' }}>
                    {lang.level}
                  </Typography>
                </Box>
              ))}
            </SectionCard>
          </Grid>
        </Grid>
      </Container>
    </CVContainer>
  );
};

export default CVPage;